import { useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Camera, Image as ImageIcon, FileText, Loader2, RefreshCw, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { fileToDataUrl, errMsg } from './guiasUtils';

const ACEITOS = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'application/pdf'];
const hoje = () => new Date().toISOString().slice(0, 10);

// Especialidades (multi): uma guia por atendimento, com tipo física/digital, data, tipo de documento e observação; aceita vários arquivos.
// Técnicos: 1 guia por paciente/mês (fluxo original, sem campos extras).
export const UploadGuiaDialog = ({ open, onClose, paciente, profKey, competencia, onSaved, multi = false, tiposGuia, tiposDocumento, guiaAlvo = null }) => {
  const { api } = useAuth();
  const camRef = useRef(null);
  const fileRef = useRef(null);
  const [arquivos, setArquivos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [progresso, setProgresso] = useState(null);
  const dataPadrao = competencia && competencia.slice(0, 7) === hoje().slice(0, 7) ? hoje() : `${(competencia || hoje()).slice(0, 7)}-01`;
  const [meta, setMeta] = useState({ tipo_guia: 'fisica', data_atendimento: dataPadrao, tipo_documento: 'guia_atendimento', observacao: '' });

  const reset = () => { setArquivos([]); setProgresso(null); setMeta({ tipo_guia: 'fisica', data_atendimento: dataPadrao, tipo_documento: 'guia_atendimento', observacao: '' }); };
  const substituindo = !!guiaAlvo || (!multi && !!paciente?.guia);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const novos = [];
    for (const file of files) {
      if (!ACEITOS.includes(file.type)) { toast.error(`${file.name}: formato não permitido. Use JPG, PNG ou PDF.`); continue; }
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name}: excede 10 MB`); continue; }
      try { novos.push({ preview: await fileToDataUrl(file), nome: file.name }); } catch { toast.error(`Não foi possível ler ${file.name}`); }
    }
    if (!novos.length) return;
    setArquivos((a) => (multi && !substituindo ? [...a, ...novos] : [novos[0]]));
  };

  const save = async () => {
    if (!arquivos.length) return;
    if (multi && !meta.data_atendimento) return toast.error('Informe a data do atendimento');
    setSaving(true); setProgresso(0);
    let enviados = 0;
    try {
      for (const a of arquivos) {
        await api.post(`/guias/profissionais/${profKey}/guias`, {
          paciente_id: paciente.paciente_id, internacao_id: paciente.internacao_id, competencia,
          arquivo: a.preview, arquivo_nome: a.nome,
          ...(multi ? { ...meta, observacao: meta.observacao || null, guia_id: guiaAlvo?.id || null } : {}),
        });
        enviados += 1; setProgresso(Math.round((enviados / arquivos.length) * 100));
      }
      toast.success(substituindo ? `Guia de ${paciente.paciente_nome} substituída` : `${enviados} guia(s) de ${paciente.paciente_nome} enviada(s)`);
      reset(); onSaved(); onClose();
    } catch (e) {
      toast.error(errMsg(e, 'Erro ao enviar guia') + (enviados ? ` (${enviados} de ${arquivos.length} enviada(s))` : ''));
      if (enviados) { onSaved(); setArquivos((arr) => arr.slice(enviados)); }
    } finally { setSaving(false); setProgresso(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) { reset(); onClose(); } }}>
      <DialogContent className="max-w-lg w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="upload-guia-dialog">
        <DialogHeader>
          <DialogTitle>{substituindo ? 'Substituir guia' : multi ? 'Enviar guia(s) de atendimento' : 'Anexar guia'}</DialogTitle>
          <DialogDescription>Paciente: <b>{paciente?.paciente_nome}</b>{multi && guiaAlvo?.data_atendimento ? ` · atendimento de ${guiaAlvo.data_atendimento.split('-').reverse().join('/')}` : ''}</DialogDescription>
        </DialogHeader>

        <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFiles} data-testid="input-guia-camera" />
        <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple={multi && !substituindo} className="hidden" onChange={handleFiles} data-testid="input-guia-arquivo" />

        {multi && (
          <div className="grid grid-cols-2 gap-3" data-testid="campos-especialidade">
            <div className="col-span-2">
              <Label>Tipo da guia</Label>
              <div className="flex gap-2 mt-1">
                {Object.entries(tiposGuia || { fisica: 'Guia física', digital: 'Guia digital' }).map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setMeta({ ...meta, tipo_guia: k })} data-testid={`tipo-guia-${k}`}
                    className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${meta.tipo_guia === k ? 'border-primary bg-primary/10 text-primary' : 'hover:bg-muted'}`}>
                    {k === 'fisica' ? <Camera className="h-4 w-4 inline mr-1" /> : <FileText className="h-4 w-4 inline mr-1" />}{l}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">{meta.tipo_guia === 'fisica' ? 'Foto ou digitalização da guia física usada no atendimento.' : 'Documento recebido já em formato digital.'}</p>
            </div>
            <div><Label>Data do atendimento</Label><Input type="date" value={meta.data_atendimento} min={`${competencia.slice(0, 7)}-01`} max={hoje()} onChange={(e) => setMeta({ ...meta, data_atendimento: e.target.value })} data-testid="input-data-atendimento" /></div>
            <div><Label>Tipo de documento</Label>
              <select className="w-full h-9 rounded-md border bg-background px-2 text-sm" value={meta.tipo_documento} onChange={(e) => setMeta({ ...meta, tipo_documento: e.target.value })} data-testid="select-tipo-documento">
                {Object.entries(tiposDocumento || { guia_atendimento: 'Guia de atendimento' }).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select></div>
            <div className="col-span-2"><Label>Observações (opcional)</Label><Textarea rows={2} value={meta.observacao} onChange={(e) => setMeta({ ...meta, observacao: e.target.value })} data-testid="input-observacao-guia" /></div>
          </div>
        )}

        {arquivos.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            <button type="button" onClick={() => camRef.current?.click()} data-testid="btn-tirar-foto"
              className="flex flex-col items-center justify-center gap-2 h-32 rounded-xl border-2 border-dashed border-primary/40 hover:border-primary hover:bg-primary/5 transition-colors">
              <Camera className="h-8 w-8 text-primary" />
              <span className="text-sm font-medium">Tirar foto</span>
            </button>
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="btn-escolher-arquivo"
              className="flex flex-col items-center justify-center gap-2 h-32 rounded-xl border-2 border-dashed border-muted-foreground/30 hover:border-primary hover:bg-primary/5 transition-colors">
              <div className="flex gap-2"><ImageIcon className="h-7 w-7 text-muted-foreground" /><FileText className="h-7 w-7 text-muted-foreground" /></div>
              <span className="text-sm font-medium">{multi && !substituindo ? 'Galeria / PDF (vários)' : 'Galeria / PDF'}</span>
              <span className="text-xs text-muted-foreground">JPG, PNG, PDF até 10 MB{multi && !substituindo ? ' cada' : ''}</span>
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {arquivos.length === 1 && (
              <div className="rounded-lg border bg-muted/40 overflow-hidden flex items-center justify-center min-h-[220px]" data-testid="guia-preview">
                {arquivos[0].preview.startsWith('data:application/pdf')
                  ? <iframe title="preview-pdf" src={arquivos[0].preview} className="w-full h-[45vh]" />
                  : <img src={arquivos[0].preview} alt="Pré-visualização" className="max-h-[45vh] w-auto object-contain" />}
              </div>
            )}
            <ul className="divide-y rounded-lg border text-sm" data-testid="lista-arquivos">
              {arquivos.map((a, i) => (
                <li key={i} className="flex items-center gap-2 px-3 py-1.5">
                  {a.preview.startsWith('data:application/pdf') ? <FileText className="h-4 w-4 text-muted-foreground" /> : <ImageIcon className="h-4 w-4 text-muted-foreground" />}
                  <span className="flex-1 truncate">{a.nome}</span>
                  {!saving && <button type="button" onClick={() => setArquivos(arquivos.filter((_, j) => j !== i))} aria-label="Remover arquivo" data-testid={`btn-remover-arquivo-${i}`}><X className="h-4 w-4" /></button>}
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{arquivos.length} arquivo(s) {multi && !substituindo ? '= ' + arquivos.length + ' guia(s)' : ''}</span>
              <div className="flex gap-1">
                {multi && !substituindo && <Button variant="ghost" size="sm" onClick={() => fileRef.current?.click()} data-testid="btn-adicionar-mais"><ImageIcon className="h-3.5 w-3.5 mr-1" />Adicionar mais</Button>}
                <Button variant="ghost" size="sm" onClick={() => setArquivos([])} data-testid="btn-trocar-arquivo"><RefreshCw className="h-3.5 w-3.5 mr-1" />Trocar</Button>
              </div>
            </div>
            {progresso !== null && <div className="h-2 w-full rounded-full bg-muted overflow-hidden" data-testid="progresso-envio"><div className="h-full bg-primary transition-[width]" style={{ width: `${progresso}%` }} /></div>}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }} disabled={saving}>Cancelar</Button>
          <Button onClick={save} disabled={!arquivos.length || saving} className="bg-green-600 hover:bg-green-700" data-testid="btn-salvar-guia">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
            {substituindo ? 'Salvar guia' : multi ? `Enviar ${arquivos.length || ''} guia(s)` : 'Salvar guia'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
